"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, Globe, FileText, Gauge, Link2, SearchCheck, Layers, Mic, Sparkles, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const servicesData = [{
  title: "Google Business Profile Optimization",
  category: "Local SEO",
  description: "I personally audit, claim, and refine your Google Business Profile to capture high-intent local map pack searches and maximize direct customer phone calls and direction requests.",
  deliverable: "Complete profile overhaul & ongoing ranking management",
  iconName: "MapPin"
}, {
  title: "Local Citation Building",
  category: "Authority",
  description: "I establish consistent Name, Address, and Phone (NAP) presence across trusted UK and global business directories to build search engine trust in your location data.",
  deliverable: "Clean citation audit & authoritative directory placements",
  iconName: "Building2"
}, {
  title: "Review Management & Reputation",
  category: "Trust & Conversion",
  description: "I set up practical review acquisition strategies that encourage genuine client feedback, helping you build market authority and improve local search conversion rates.",
  deliverable: "Automated review request workflows & response templates",
  iconName: "Star"
}, {
  title: "Local Schema Markup",
  category: "Technical SEO",
  description: "I implement precise LocalBusiness JSON-LD structured data directly into your code, giving search engines unmistakable clarity on your service areas and credentials.",
  deliverable: "Custom schema deployment & rich snippet verification",
  iconName: "Code2"
}, {
  title: "Geo-Targeted On-Page SEO",
  category: "On-Page SEO",
  description: "I optimize titles, meta descriptions, headings, and copy for specific cities and counties across the UK, ensuring high relevance for location-specific search queries.",
  deliverable: "Tailored page optimization for top commercial locations",
  iconName: "Globe"
}, {
  title: "Localized Content Strategy",
  category: "Content SEO",
  description: "I research and write tailored, expert content focused on local industry challenges, establishing your consultancy or business as the definitive regional authority.",
  deliverable: "High-value local landing pages & targeted articles",
  iconName: "FileText"
}, {
  title: "Technical Local Performance",
  category: "Core Web Vitals",
  description: "I optimize site speed, mobile user experience, and technical health so that local visitors convert quickly on mobile devices and Google rewards your site speed.",
  deliverable: "Mobile speed audit & technical code optimizations",
  iconName: "Gauge"
}, {
  title: "Hyper-Local Link Building",
  category: "Backlinks",
  description: "I earn valuable links from regional news outlets, local business partners, and industry associations, strengthening your domain's geographic relevance.",
  deliverable: "Genuine outreach campaigns & high-trust local links",
  iconName: "Link2"
}, {
  title: "Local SEO Audits & Analysis",
  category: "Diagnostics",
  description: "I conduct thorough, manual diagnostics to pinpoint exact ranking blockers, competitor advantages, and untapped revenue opportunities in your market area.",
  deliverable: "Comprehensive diagnostic report with prioritized action steps",
  iconName: "SearchCheck"
}, {
  title: "Multi-Location SEO Strategy",
  category: "Scale & Growth",
  description: "For multi-branch or national businesses, I build scalable site structures that help each location rank independently without creating internal duplicate content.",
  deliverable: "Scalable location architecture & individualized profiles",
  iconName: "Layers"
}, {
  title: "Voice & Mobile Search Setup",
  category: "Mobile First",
  description: "I optimize your conversational search queries and mobile landing pages so near-me mobile voice searches connect instantly with your business details.",
  deliverable: "Conversational keyword integration & mobile friction audit",
  iconName: "Mic"
}, {
  title: "AI Search & Maps Visibility",
  category: "Modern Search",
  description: "I prepare your local search presence for AI-driven engines like ChatGPT and Google Gemini, ensuring your brand appears in conversational local recommendations.",
  deliverable: "AI search ecosystem positioning & entity building",
  iconName: "Sparkles"
}];
const renderIcon = (name: string) => {
  const props = {
    className: "w-5 h-5 text-primary-foreground shrink-0"
  };
  switch (name) {
    case "MapPin":
      return <MapPin {...props} />;
    case "Building2":
      return <Building2 {...props} />;
    case "Star":
      return <Star {...props} />;
    case "Code2":
      return <Code2 {...props} />;
    case "Globe":
      return <Globe {...props} />;
    case "FileText":
      return <FileText {...props} />;
    case "Gauge":
      return <Gauge {...props} />;
    case "Link2":
      return <Link2 {...props} />;
    case "SearchCheck":
      return <SearchCheck {...props} />;
    case "Layers":
      return <Layers {...props} />;
    case "Mic":
      return <Mic {...props} />;
    case "Sparkles":
      return <Sparkles {...props} />;
    default:
      return <MapPin {...props} />;
  }
};
export default function Servicesgrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 transition-colors duration-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground rounded-full">
            Direct Consultant Expertise
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight">
            Specialized SEO Consultancy Services
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            When you hire YE Consultancy, you work directly with me—Yassine
            Essaadi. No junior account managers or outsourced deliverables. Here
            is how I deliver tailored search growth for your business.
          </p>

          <div className="flex items-center justify-center gap-2 pt-2 text-sm font-medium text-primary">
            <CheckCircle2 className="w-4 h-4" />
            <span>Hands-on implementation & full technical transparency</span>
          </div>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {servicesData.map((service, i) => <motion.div key={service.title} variants={cardVariants} data-index={i}>
              <Card className="h-full border border-border/80 shadow-md hover:shadow-xl transition-[box-shadow,border-color] duration-300 overflow-hidden flex flex-col justify-between group rounded-xl bg-card">
                <div>
                  {/* Category Banner */}
                  <div className="bg-primary px-6 py-5 flex items-center justify-between transition-colors duration-300">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary-foreground/10 border border-primary-foreground/20">
                        {renderIcon(service.iconName)}
                      </div>
                      <Badge variant="outline" className="border-primary-foreground/30 text-primary-foreground text-xs font-medium rounded-md px-2 py-0.5 bg-primary-foreground/10">
                        {service.category}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pt-6 pb-2 px-6">
                    <CardTitle className="text-xl font-bold font-serif text-card-foreground group-hover:text-primary transition-colors leading-snug">
                      {service.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 py-2 space-y-4">
                    <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                      {service.description}
                    </p>

                    <Separator className="bg-border/60" />

                    <div className="space-y-1">
                      <span className="text-xs font-semibold uppercase text-accent tracking-wider block">
                        Key Deliverable
                      </span>
                      <p className="text-xs font-medium text-foreground leading-snug">
                        {service.deliverable}
                      </p>
                    </div>
                  </CardContent>
                </div>

                <div className="p-6 pt-2">
                  <Button asChild variant="ghost" className="w-full justify-between hover:bg-secondary/60 text-primary font-medium text-sm group/btn px-3 rounded-lg">
                    <Link to="/ContactPage">
                      <span>Inquire About This Service</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom CTA Banner */}
        <div className="mt-16 bg-muted/60 border border-border rounded-2xl p-8 lg:p-10 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="space-y-2 max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
              Not sure which SEO strategy fits your current growth goals?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              Book a complimentary 30-minute consultation directly with me.
              I will analyze your current search presence and map out a tailored plan.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium px-6 py-3 rounded-lg text-sm sm:text-base">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}